import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { SubmitHandler, useFormContext } from 'react-hook-form';

import { SortDirectionDto } from '@/api/generated';
import { DateFormField } from '@/components/form/DateFormField.comp.tsx';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp';
import { TextFormField } from '@/components/form/TextFormField.comp';
import { CustomTableAdditionalButtonsModel } from '@/components/table/components/formBuilder/model/CustomTableAdditionalButtons.model.ts';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { useCustomTableContext } from '@/components/table/context/CustomTable.context';
import { CustomTableVariant } from '@/components/table/CustomTable.comp.tsx';
import { CustomTableStateDataModel } from '@/components/table/model/CustomTableState.model.ts';
import { AnnouncementAnchor } from '@/contexts/announcements/AnnouncementAnchor.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface BasicRequestProps<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
> {
  page: number;
  pageSize: number;
  params: SEARCH_FORM_MODEL;
  sortBy?: SORT_BY_COLUMN_NAME_MODEL;
  sortOrder?: SortDirectionDto;
}

interface Props<
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
> {
  additionalButtons: CustomTableAdditionalButtonsModel<SEARCH_FORM_MODEL, BUTTONS_PARAMS>[];
  filters: CustomTableFilterDefinition<SEARCH_FORM_MODEL>[];
  formRequest: (
    req: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>,
  ) => Promise<CustomTableStateDataModel<ROW_ITEM_MODEL>>;
  initParams: BasicRequestProps<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL>;
  isLoading: boolean;
  size?: CustomTableVariant;
}

export const CustomTableFormBuilder = <
  SORT_BY_COLUMN_NAME_MODEL extends string,
  SEARCH_FORM_MODEL extends object,
  ROW_ITEM_MODEL extends object,
  BUTTONS_PARAMS extends object = never,
>({
  additionalButtons,
  filters,
  formRequest,
  initParams,
  isLoading,
  size,
}: Props<SORT_BY_COLUMN_NAME_MODEL, SEARCH_FORM_MODEL, ROW_ITEM_MODEL, BUTTONS_PARAMS>) => {
  const { customTableState, setCustomTableState } = useCustomTableContext<
    SORT_BY_COLUMN_NAME_MODEL,
    SEARCH_FORM_MODEL,
    ROW_ITEM_MODEL
  >();
  const formMethods = useFormContext<SEARCH_FORM_MODEL>();
  const { t } = useTranslationWithPrefix('components.table.content');

  const handleSubmitWrapper = async () => {
    await formMethods.handleSubmit(handleSubmit)();
  };

  const handleSubmit: SubmitHandler<SEARCH_FORM_MODEL> = async from => {
    const data = await formRequest({
      ...customTableState,
      page: 0,
      params: from,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: 0,
      params: formMethods.getValues(),
    }));
  };

  const handleResetForm = async () => {
    formMethods.reset(initParams.params);

    const data = await formRequest({
      ...customTableState,
      page: 0,
      params: initParams.params,
      sortBy: initParams.sortBy,
      sortOrder: SortDirectionDto.Asc,
    });

    setCustomTableState(cur => ({
      ...cur,
      data: {
        ...data,
      },
      page: 0,
      params: initParams.params,
      sortBy: initParams.sortBy!,
      sortOrder: SortDirectionDto.Asc,
    }));
  };

  const resolveField = (field: CustomTableFilterDefinition<SEARCH_FORM_MODEL>) => {
    const { announcementAnchorKey, disabled, type } = field;
    const isDisabled = disabled ?? isLoading;
    let resolvedField: React.ReactNode;

    if (type === CustomTableFilterTypeEnum.Text) {
      resolvedField = <TextFormField {...field} disabled={isDisabled} />;
    }
    if (type === CustomTableFilterTypeEnum.Select) {
      resolvedField = <SelectSearchFormField {...field} disabled={isDisabled} />;
    }
    if (type === CustomTableFilterTypeEnum.Date) {
      resolvedField = <DateFormField {...field} disabled={isDisabled} />;
    }
    if (!announcementAnchorKey) {
      return resolvedField;
    }

    return (
      <AnnouncementAnchor
        anchorKey={announcementAnchorKey}
        component="div"
        sx={{ display: 'block', lineHeight: 0 }}
      >
        {resolvedField}
      </AnnouncementAnchor>
    );
  };

  return (
    <>
      <Grid container spacing={size === 'small' ? 1 : 2} sx={{ pt: 1 }}>
        {filters.map(field => (
          <Grid key={field.name} size={field.xs}>
            {resolveField(field)}
          </Grid>
        ))}
      </Grid>
      <Stack
        direction="row"
        spacing={size === 'small' ? 1 : 2}
        sx={{
          justifyContent: 'space-between',
          pb: size === 'small' ? 1 : 2,
        }}
      >
        <Stack
          direction="row"
          spacing={size === 'small' ? 1 : 2}
          sx={{ pt: size === 'small' ? 1 : 2 }}
        >
          {additionalButtons.map(additionalButton => (
            <additionalButton.button
              handleGetValues={formMethods.getValues}
              handleSearch={handleSubmitWrapper}
              isLoading={isLoading}
              key={additionalButton.name}
              params={additionalButton.params}
            />
          ))}
        </Stack>
        <Stack
          direction="row"
          spacing={size === 'small' ? 1 : 2}
          sx={{ pt: size === 'small' ? 1 : 2 }}
        >
          <Button disabled={isLoading} onClick={handleResetForm} variant="outlined">
            {t('button.resetForm')}
          </Button>
          <Button disabled={isLoading} onClick={handleSubmitWrapper}>
            {t('button.submitSearch')}
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
